import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { ArrowLeft, Sun, Layers, Eye, Shield, Palette, FileText, ArrowRight, Sparkles, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const tools = [
  {
    icon: Eye,
    title: "Design EyeQ® Visualizer",
    description: "Upload a photo of your home and try on different Owens Corning shingle colors virtually. See your new roof before you commit.",
    href: "/tools/design-eyeq",
    cta: "Open Visualizer",
    featured: true,
  },
  {
    icon: Sun,
    title: "Solar Savings Calculator",
    description: "See your estimated savings based on real NM solar data, current incentives, and local electricity rates.",
    href: "/tools/solar-calculator",
    cta: "Open Calculator",
  },
  {
    icon: Layers,
    title: "Shingle Explorer",
    description: "Browse Owens Corning® shingle product lines and colors on different house styles.",
    href: "/tools/shingle-explorer",
    cta: "Explore Shingles",
  },
  {
    icon: Shield,
    title: "Total Protection System®",
    description: "Learn how the integrated system of components helps your roof Seal, Defend, and Breathe.",
    href: "/tools/total-protection",
    cta: "Explore the System",
  },
  {
    icon: Palette,
    title: "Design & Inspire",
    description: "Explore curated shingle and color pairings to find the perfect aesthetic for your home.",
    href: "/tools/design-inspire",
    cta: "Get Inspired",
  },
  {
    icon: Calculator,
    title: "Instant Estimate Calculator",
    description: "Answer a few questions about your roofing, solar, construction, or mold project and get an estimated price range based on 2026 NM market rates.",
    href: "/tools/estimate-calculator",
    cta: "Get Estimate",
    featured: false,
  },
  {
    icon: FileText,
    title: "Build Your Roof®",
    description: "Interactively build your new roof step-by-step, selecting your preferred Owens Corning roofing products.",
    href: "https://www.owenscorning.com/en-us/roofing/build-your-roof/customize",
    cta: "Start Building",
    external: true,
  },
];

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://zenithsolutionsnm.com/" },
    { "@type": "ListItem", position: 2, name: "Homeowner Tools", item: "https://zenithsolutionsnm.com/tools" },
  ],
};

const ToolsPage = () => (
  <>
    <Helmet>
      <title>Free Roofing & Solar Homeowner Tools | Zenith Solutions Albuquerque</title>
      <meta name="description" content="Free homeowner tools: Solar savings calculator, Owens Corning shingle explorer, roof visualizer, and more. Plan your roofing or solar project in Albuquerque NM." />
      <link rel="canonical" href="https://zenithsolutionsnm.com/tools" />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>
    <Navbar />
    <div className="pt-24 pb-20 min-h-screen bg-section-light">
      <div className="container mx-auto max-w-5xl px-4">
        <nav aria-label="Breadcrumb" className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <p className="text-sm font-bold uppercase tracking-widest text-primary mb-3">Exclusive Resources</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-3">
            Free Homeowner Tools
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed text-lg">
            Interactive tools most contractors don't offer — explore options and make confident decisions, no commitment required.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool, i) => {
            const isFeatured = 'featured' in tool && tool.featured;
            const cardClasses = `group flex flex-col h-full rounded-2xl border shadow-sm p-6 hover:shadow-lg transition-all ${isFeatured ? "bg-primary/5 border-primary/30 sm:col-span-2 lg:col-span-3" : "bg-card hover:border-primary/30"}`;
            
            const inner = isFeatured ? (
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <tool.icon className="h-10 w-10 text-primary" />
                </div>
                <div className="text-center md:text-left flex-1">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <h2 className="text-xl md:text-2xl font-black text-foreground">{tool.title}</h2>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-2 py-0.5">
                      <Sparkles className="h-3 w-3" /> Most Popular
                    </span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-4">{tool.description}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary group-hover:underline">
                    {tool.cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4 group-hover:bg-primary/20 transition-colors">
                  <tool.icon className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-lg font-bold text-foreground mb-2">{tool.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{tool.description}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary group-hover:underline">
                  {tool.cta} {'external' in tool && tool.external ? <span aria-hidden>↗</span> : <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                </span>
              </>
            );

            return (
              <motion.div
                key={tool.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={isFeatured ? "sm:col-span-2 lg:col-span-3" : ""}
              >
                {'external' in tool && tool.external ? (
                  <a href={tool.href} target="_blank" rel="noopener noreferrer" className={cardClasses}>{inner}</a>
                ) : (
                  <Link to={tool.href} className={cardClasses}>{inner}</Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
    <FooterSection />
  </>
);

export default ToolsPage;
